// Kodierung und Formatierung der Kennzahlen-Werte.
//
// Reine Funktionen, kein React — damit die Aggregation (kpiAggregate) und die
// Tests ohne DOM auskommen.

import type { Decoded, ExtraData, FieldType, KPIYear, KPIYearPayload } from "./kpiTypes";

export const decode = (k: KPIYear): Decoded => {
  let extra: ExtraData = {};
  try {
    if (k.otherInfo) extra = JSON.parse(k.otherInfo) as ExtraData;
  } catch {
    extra = {};
  }
  return { ...k, ...extra };
};

/** Leeres Feld → `null` (nicht `undefined`), damit es im JSON-Body landet. */
const numOrNull = (v: unknown): number | null => {
  if (v === undefined || v === null || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

export const encode = (d: Decoded): KPIYearPayload => {
  const {
    year,
    revenueTotal,
    cogsEur,
    grossProfit,
    avgConcurrentPersons,
    otherInfo: _ignored,
    ...rest
  } = d;
  // strip undefined keys so JSON is compact — die Extras werden als ganzer Blob
  // ersetzt, ein weggelassener Key ist dort also bereits „geleert".
  const extra: Record<string, any> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v === undefined || v === null || v === "") continue;
    extra[k] = v;
  }
  // Geleerte Felder gehen explizit als `null` raus (Gegenstück zur null-Semantik
  // im Backend): `undefined` verschwindet in JSON.stringify, der Key fehlte im
  // Body und der alte Wert bliebe stehen. Das gilt auch für `otherInfo` — sonst
  // überlebt der letzte Extras-Blob das Leeren aller qualitativen Felder.
  return {
    year,
    revenueTotal: numOrNull(revenueTotal),
    cogsEur: numOrNull(cogsEur),
    grossProfit: numOrNull(grossProfit),
    avgConcurrentPersons: numOrNull(avgConcurrentPersons),
    otherInfo: Object.keys(extra).length > 0 ? JSON.stringify(extra) : null,
  };
};

export const fmtEur = (v: number | undefined): string => {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  try {
    return v.toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
  } catch {
    return `${v} €`;
  }
};

/**
 * Prozentwert für die Anzeige: „45", „45%" und „45 %" ergeben alle „45 %".
 *
 * Die Quoten stehen als ROHER STRING im `otherInfo`-Blob — je nachdem, wo
 * zuletzt erfasst wurde, mit oder ohne Zeichen. Ein blindes `${v}%` machte aus
 * „45 %" ein „45 %%".
 */
export const fmtPct = (v: string | number | undefined | null): string | undefined => {
  const s = String(v ?? "").trim();
  if (!s) return undefined;
  return s.endsWith("%") ? s.replace(/\s*%$/, " %") : `${s} %`;
};

/**
 * Sektions-Zusammenfassung aus Betrag UND Quote — „4.950.000 € · 45 %".
 *
 * Die Sektionszeile ist das, was bei zugeklappter Sektion sichtbar bleibt, und
 * der Reiter startet zugeklappt. Beim Wareneinsatz ist die Quote die Zahl, die
 * der Berater sucht: Ein Betrag ohne Bezugsgröße sagt nichts, 45 % sagt sofort,
 * ob der Betrieb Handel oder Fertigung ist.
 *
 * Fehlt eine der beiden, steht die andere allein da — insbesondere die Quote
 * ohne Betrag. Genau der Fall kommt aus dem Erfassen-Wizard regelmäßig, und
 * eine Sektion, die „—" meldet, obwohl eine Zeile darunter eine Zahl steht,
 * liest sich als „nichts erfasst".
 */
export const fmtEurMitQuote = (
  betrag: number | undefined,
  quote: string | number | undefined | null,
): string => {
  const q = fmtPct(quote);
  const hatBetrag = betrag !== undefined && betrag !== null && !Number.isNaN(betrag);
  if (hatBetrag && q) return `${fmtEur(betrag)} · ${q}`;
  if (hatBetrag) return fmtEur(betrag);
  return q ?? "—";
};

/**
 * Stückzahl im deutschen Format („2,5", „1.200").
 *
 * `String(n)` taugt dafür nicht: eine Summe aus „2,5" und „3" erschiene als
 * „5.5" — im selben Popover, in dem die Einzelwerte deutsch mit Komma stehen.
 * Punkt und Komma nebeneinander als Dezimaltrenner ist genau die Zweideutigkeit,
 * die diese Tabelle sonst überall vermeidet.
 */
export const fmtNum = (v: number): string =>
  Number.isFinite(v) ? v.toLocaleString("de-DE", { maximumFractionDigits: 2 }) : "—";

export const parseEur = (raw: string): number | undefined => {
  if (!raw) return undefined;
  const cleaned = raw.replace(/[^\d,.\-]/g, "").replace(/\./g, "").replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
};

// Zahl im deutschen Format: optionales Vorzeichen, Tausenderpunkte in Dreier-
// Gruppen, ein Dezimalkomma, Währungssymbol vorn oder hinten, Leerzeichen egal.
const EURO_STRICT_RE = /^([+-])?\s*(?:€|EUR)?\s*(\d{1,3}(?:\.\d{3})*|\d+)(?:,(\d+))?\s*(?:€|EUR)?$/i;

/**
 * Strenger Parser für Summen (Beträge UND Stückzahlen): liefert nur bei einer
 * eindeutigen Zahl im deutschen Format einen Wert, sonst `null`.
 *
 * `parseEur` taugt dafür nicht: es strippt alle Nicht-Ziffern und macht aus
 * "ca. 1,2 Mio" die Zahl 12. In einem Eingabefeld ist das harmlos (der Nutzer
 * sieht sofort, was ankommt) — in einer Verbund-Summe wäre es ein stiller
 * Fehler: die Summe sähe vollständig aus und läge um Größenordnungen daneben.
 * Nicht Parsebares muss sichtbar bleiben (→ AggCell.kind "mixed") statt heimlich
 * mitgerechnet zu werden.
 *
 * `parseNum` taugt genauso wenig — und zwar auch für Stückzahlen: es liest den
 * deutschen Tausenderpunkt als Dezimalpunkt („1.200" → 1,2). Die Zahlfelder des
 * Erfassen-Wizards landen als ROHER STRING im `otherInfo`-Blob, „1.200
 * Mitarbeiter" ist dort also ein realistischer Inhalt.
 */
export const parseEuroStrict = (v: unknown): number | null => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  // Geschützte Leerzeichen stammen aus toLocaleString-formatierten Werten
  // ("1.000 €" mit U+00A0 bzw. U+202F vor dem Symbol).
  const raw = v.replace(/[  ]/g, " ").trim();
  if (!raw) return null;
  const m = EURO_STRICT_RE.exec(raw);
  if (!m) return null;
  const [, sign, int, frac] = m;
  const n = Number(`${int.replace(/\./g, "")}.${frac ?? "0"}`);
  if (!Number.isFinite(n)) return null;
  return sign === "-" ? -n : n;
};

/**
 * Welche Zeilen im Verbund summiert werden dürfen: nur Beträge und Stückzahlen.
 *
 * Prozentfelder (`revenueMargin`, `cogsPct`, `equityPct`, die `*_bereich_*`-Zeilen,
 * `subunternehmer_umsatzanteil`) sind bewusst NIE summierbar — eine addierte
 * Prozentzahl ist immer falsch, und ein gewichtetes Mittel lässt sich aus den
 * gespeicherten Feldern nicht herleiten: der Gewinn wird nirgends erfasst, die
 * Umsatzrendite des Verbunds ist aus den Zeilen also nicht rekonstruierbar.
 *
 * Einzige Ausnahme wäre `cogsPct`: die ließe sich exakt als Σ cogsEur / Σ revenueTotal
 * konsolidieren. Das wäre ein eigener Modus `agg: "ratio"` mit Zähler-/Nenner-Zeile
 * — bewusst nicht in diesem PR.
 *
 * ACHTUNG: `"number"` heißt „Stückzahl", nicht „irgendeine Zahl". Pro-Kopf- bzw.
 * Bezugsgrößen (z. B. `ma_monatsstunden` = Monatsstunden EINER Vollzeitstelle)
 * sind wie Prozente nicht additiv und brauchen deshalb ein explizites
 * `agg: "none"` an der Zeile.
 */
export const defaultAgg = (type: FieldType): "sum" | "none" =>
  type === "euro" || type === "number" ? "sum" : "none";
